
import { isImageTargetUrl } from './imageUtils';

// Patterns that indicate the user is requesting feedback on their impressions
const FEEDBACK_REQUEST_PATTERNS = [
  /feedback/i,
  /analyze/i,
  /analysis/i,
  /how did I do/i,
  /how was that/i,
  /what do you think/i,
  /evaluate/i,
  /assessment/i,
  /categorize/i,
  /matches/i,
  /mismatches/i,
  /correct/i,
  /wrong/i,
  /accurate/i
];

export const isFeedbackRequest = (message: string): boolean => {
  return FEEDBACK_REQUEST_PATTERNS.some(pattern => pattern.test(message));
};

export const constructFeedbackPrompt = (userMessage: string, formattedNotes: string, targetUrl?: string): string => {
  const systemPrompt = `[SYSTEM-MODE: ANALYSIS-ONLY]
You are an expert remote viewing task analyst. Your job is to analyze the user's notes against the provided target.
- DO NOT generate a new target.
- DO NOT modify any target data.
- Your analysis must be based *only* on the context provided below.
`;

  if (!formattedNotes && !targetUrl) {
    return `${systemPrompt}\n\nUser instructions: ${userMessage}`;
  }

  // Start with the system prompt and the user's core request
  let enhancedPrompt = `${systemPrompt}\n\nUser instructions: ${userMessage}`;

  // Add the full context block
  enhancedPrompt += `\n\n--- CONTEXT FOR ANALYSIS ---`;

  // Add the user's impressions/notes with clear labeling
  if (formattedNotes) {
    enhancedPrompt += `\n\nYOUR RECORDED IMPRESSIONS AND NOTES:\n${formattedNotes}`;
  }

  // Add the target information with specific instructions based on target type
  if (targetUrl) {
    enhancedPrompt += `\n\nREVEALED TARGET: ${targetUrl}`;
    
    // Check if this is an image target
    if (isImageTargetUrl(targetUrl)) {
      enhancedPrompt += `\n\nINSTRUCTIONS FOR VISUAL ANALYSIS: This target is an image from Picsum Photos. You MUST use your vision capabilities to load and visually analyze the actual image at the provided URL. The user wrote their impressions before seeing this target image. 

CRITICAL: You must actually VIEW the image at the URL to provide accurate feedback. Do not guess, hallucinate, or provide generic responses. Load the image and describe exactly what you see.

Compare what you actually see in the image with the user's recorded impressions above. Provide detailed feedback directly addressing what the user wrote, using second person language ("You described...", "You mentioned...", "Your impression of..."). 

Format your response as clear, readable markdown with proper headers and bullet points. Structure your analysis with these sections:

## 📊 Impressions Overview
Brief summary of what the user described.

## ✅ Accurate Impressions
What the user got right or close to correct.

## ❌ Mismatches
What doesn't align with the actual image.

## 🔄 Partial Connections
Elements that have some connection but aren't exact matches.

## 💡 Additional Observations
Notable elements in the image the user didn't mention.

Analyze and compare:
- Visual elements: colors, objects, people, animals, scenery, architecture, etc.
- Spatial relationships: positioning, composition, foreground/background
- Lighting and atmosphere: bright/dark, mood, time of day
- Any specific details the user mentioned vs what's actually visible
- Emotional impressions vs the actual mood/feeling of the image

Be specific about what matches, what doesn't match, and what partial connections exist between the user's impressions and the actual visual content of the image. If you cannot see the image or are having trouble loading it, explicitly state this rather than guessing.`;
    } else if (targetUrl.includes('google.com/maps') || targetUrl.includes('goo.gl/maps')) {
      enhancedPrompt += `\n\nINSTRUCTIONS FOR VIEW ANALYSIS: You MUST use your vision capabilities to analyze the Street View imagery at this Google Maps location. Navigate to the URL and visually examine what is shown in the Street View. The user wrote their impressions before seeing this target location.

CRITICAL: You must actually ACCESS and VIEW the Street View imagery at the Google Maps URL to provide accurate feedback. Do not guess or provide generic location-based responses. Load the Street View and describe exactly what you can see in the imagery.

Compare what you actually see in the Street View with the user's recorded impressions above. Provide detailed feedback directly addressing what the user wrote, using second person language ("You described...", "You mentioned...", "Your impression of..."). 

Format your response as clear, readable markdown with proper headers and bullet points. Structure your analysis with these sections:

## 📊 Impressions Overview
Brief summary of what the user described.

## ✅ Accurate Impressions
What the user got right or close to correct about the location.

## ❌ Mismatches
What doesn't align with the actual Street View imagery.

## 🔄 Partial Connections
Elements that have some connection but aren't exact matches.

## 💡 Additional Observations
Notable elements visible in the Street View that the user didn't mention.

Analyze and compare:
- Road/path characteristics: surface type, condition, width, markings
- Vegetation: trees, grass, landscaping, overgrowth
- Structures: buildings, signs, barriers, utilities
- Environmental features: terrain, weather conditions, lighting
- Spatial relationships: what's on the sides, ahead, behind
- Urban vs rural characteristics
- Any specific details the user mentioned vs what's actually visible in the Street View

Be specific about what matches, what doesn't match, and what partial connections exist between the user's impressions and what you can actually see in the Street View imagery. If you cannot access the Street View or are having trouble loading it, explicitly state this rather than guessing.`;
    } else {
      // Generic URL - provide general analysis instructions
      enhancedPrompt += `\n\nINSTRUCTIONS: Please analyze the content at this URL and compare it with the user's recorded impressions above. The user wrote these impressions before seeing the target. 

Format your response as clear, readable markdown with proper headers and bullet points. Provide detailed feedback directly addressing what the user wrote, using second person language ("You described...", "You mentioned...", "Your impression of..."). Analyze matches, mismatches, and partial connections between what the user described and the actual content.`;
    }
  }

  return enhancedPrompt;
};
